import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

const BILLING_MONTHS = [3, 6, 9, 12]
const BILLING_DAY = 15
const TIMEZONE = 'Asia/Tokyo'

export const isBillingDate = (date: Date): boolean => {
  const d = dayjs(date).tz(TIMEZONE)
  // dayjs().month() is 0-indexed
  return BILLING_MONTHS.includes(d.month() + 1) && d.date() === BILLING_DAY
}

/**
 * 請求日に対応する「請求対象期間の終わり(periodEnd)」を返す。
 *
 * 【なぜ請求日の前日なのか】
 * 請求日(例: 6/15)は「その日付で請求が確定・発行される日」を表す。請求はその日の朝には
 * 確定していなければならないため、請求対象は請求日の前日(JST)までとし、請求日当日の利用分は
 * 次回請求に回す。すなわち請求対象期間は `[前回請求日, 今回請求日)` のJST半開区間であり、
 * その終わり(periodEnd)＝最終対象日は「請求日の前日(JST)」となる(例: 6/15請求 → 6/14)。
 * 「請求日(6/15)」と「対象期間の終わり(6/14)」は別概念である点に注意。
 *
 * 【返り値の性質】
 * 返り値は「請求日の前日(JST)の末尾の瞬間」を表すDateで、次の2用途を同時に満たす。
 * - Invoice.periodEnd(Prismaの`@db.Date`, UTC切り捨て)に保存すると請求日の前日(例: 6/14)になる
 * - `createdAt <= periodEnd` の集計カットオフに使うと、JSTで前日終了時点(請求日0時の直前)までを含む
 *
 * 【タイムゾーン非依存】
 * 計算は明示的にAsia/Tokyoで行う。以前はローカルタイムゾーンの`startOf('day')`を用いていたため、
 * 実行環境(UTC等)によって保存される日付が1日ずれる不具合があった。
 */
export const getBillingPeriodEnd = (billingDate: Date): Date => {
  // 請求日(JST)の0時の直前 = 前日(JST)の23:59:59.999
  return dayjs(billingDate).tz(TIMEZONE).startOf('day').subtract(1, 'millisecond').toDate()
}

/**
 * 与えられた瞬間のJSTでの暦日を、Prismaの`@db.Date`カラム保存用のDate(UTC 0時)として返す。
 *
 * `@db.Date`はUTCで日付部分を切り捨てて保存するため、タイムスタンプ(瞬間)をそのまま渡すと
 * JST 0時〜9時の値が前日の日付として保存されてしまう。JSTでの暦日を保存したい場合は
 * この関数で「JSTのYYYY-MM-DDをUTC 0時として表すDate」に変換してから渡す
 * (getNextBillingDateが返す請求日と同じ表現方法)。
 */
export const toJstCalendarDate = (date: Date): Date => {
  return dayjs.utc(dayjs(date).tz(TIMEZONE).format('YYYY-MM-DD')).toDate()
}

/**
 * 指定された日付以降の、直近の請求日を返す
 * 指定された日付が請求日の場合は、その日を返す
 * 結果はUTCのDateオブジェクトとして返すが、実質的な日付はJSTで計算される
 */
export const getNextBillingDate = (date: Date): Date => {
  const d = dayjs(date).tz(TIMEZONE)
  const currentYear = d.year()
  const currentMonth = d.month() + 1 // 1-indexed
  const currentDay = d.date()

  // 今年の候補日を探す
  for (const month of BILLING_MONTHS) {
    if (month > currentMonth || (month === currentMonth && currentDay <= BILLING_DAY)) {
      // NOTE: GraphQLのDate ScalarはUTCのDateオブジェクトをISO文字列化してYYYY-MM-DD部分を取得するため、
      // ここで返すDateオブジェクトは「UTCでのYYYY-MM-DD」がJSTでの請求日と一致するようにする。
      // つまり、JSTで計算した請求日(YYYY-MM-15)を、時刻情報を捨ててUTCのYYYY-MM-15T00:00:00Zとして返す。
      return dayjs
        .utc()
        .year(currentYear)
        .month(month - 1)
        .date(BILLING_DAY)
        .startOf('day')
        .toDate()
    }
  }

  // 今年に候補がない場合は、翌年の最初の候補日（3月15日）
  const firstBillingMonth = BILLING_MONTHS[0]
  if (firstBillingMonth === undefined) {
    throw new Error('BILLING_MONTHS must not be empty')
  }
  return dayjs
    .utc()
    .year(currentYear + 1)
    .month(firstBillingMonth - 1)
    .date(BILLING_DAY)
    .startOf('day')
    .toDate()
}

/**
 * サーバー利用月数を計算する
 * 利用開始月を1ヶ月目とし、締め日までの月数を計算する
 *
 * @param startDate 利用開始日
 * @param periodEnd 締め日
 * @returns 利用月数 (1以上)
 */
export function calculateServerUsageMonths(startDate: Date, periodEnd: Date): number {
  const monthsDiff = dayjs(periodEnd).diff(dayjs(startDate), 'month')
  return Math.max(0, monthsDiff) + 1
}
